import { ScopeButton } from '@monorepo/shared-lib';
import GuideStepOne from 'assets/images/excel-entity-guide-step1.svg';
import GuideStepTwo from 'assets/images/excel-entity-guide-step2.svg';
import GuideStepThree from 'assets/images/excel-entity-guide-step3.svg';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import { StyledCard } from 'components/Custom/card';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Preference, PreferenceActions } from 'utils/preferences';

export const EXCEL_ENTITY_TUTORIAL_HIDE = 'excel-entity-tutorial-hide';

interface ExcelEntityGuideProps {
  setShowCard: (show: boolean) => void;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
}

const GuideContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 20px 40px 10px;
  background: var(--color-surface-raised, #ffffff);
  gap: 40px;

  @media (max-width: 1200px) {
    flex-wrap: wrap;
  }
`;

const StepContent = styled.div`
  margin-bottom: 30px;
  min-height: 90px;
`;

const StepTitle = styled.h3`
  font-family: Inter;
  font-size: 18px;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: 0%;
  color: var(--color-text-base, #2e2e2e);
  margin-bottom: 8px;
`;

const StepDescription = styled.p`
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0%;
  color: var(--color-text-muted, #5a6a6a);
  margin: 0;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: flex-start;

  img {
    width: 80%;
    height: auto;
  }
`;

const GuideStep = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 320px;
  height: 100%;

  &:first-child ${StepContent} {
    margin-bottom: 14px;
  }
`;

export const ExcelEntityGuide: React.FC<ExcelEntityGuideProps> = ({
  setShowCard,
  handlePreferences,
}) => {
  const { t } = useTranslation();
  const handleClose = () => {
    setShowCard(false);
    handlePreferences(PreferenceActions.SET, {
      name: EXCEL_ENTITY_TUTORIAL_HIDE,
      value: true,
    });
  };

  return (
    <StyledCard
      width="100%"
      spreadcontent
      fullsize
      hideheader
      style={{
        marginBottom: '20px',
        position: 'relative',
        background: 'var(--color-surface-raised, #FFFFFF)',
      }}
      extra={
        <IconClose
          role="button"
          tabIndex={0}
          aria-label={t('common.close')}
          style={{
            top: '25',
            position: 'absolute',
            right: '25',
            cursor: 'pointer',
            width: '18px',
            height: '18px',
            filter:
              'invert(71%) sepia(12%) saturate(247%) hue-rotate(131deg) brightness(80%) contrast(81%)',
          }}
          onClick={handleClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClose();
            }
          }}
        />
      }
    >
      <GuideContainer>
        <GuideStep>
          <StepContent>
            <StepTitle>{t('organisation.guide-excel-step1-title')}</StepTitle>
            <StepDescription>
              {t('organisation.guide-excel-step1-desc')}
            </StepDescription>
          </StepContent>
          <ImageContainer>
            <img
              src={GuideStepOne}
              alt={t('organisation.guide-excel-step1-title')}
            />
          </ImageContainer>
        </GuideStep>

        <GuideStep>
          <StepContent>
            <StepTitle>{t('organisation.guide-excel-step2-title')}</StepTitle>
            <StepDescription>
              {t('organisation.guide-excel-step2-desc')}
            </StepDescription>
          </StepContent>
          <ImageContainer>
            <img
              src={GuideStepTwo}
              alt={t('organisation.guide-excel-step2-title')}
            />
          </ImageContainer>
        </GuideStep>

        <GuideStep>
          <StepContent>
            <StepTitle>{t('submissions.add-a-tax-return')}</StepTitle>
            <StepDescription>
              {t('organisation.guide-excel-step3-desc')}
            </StepDescription>
          </StepContent>
          <ImageContainer>
            <img
              src={GuideStepThree}
              alt={t('submissions.add-a-tax-return')}
            />
          </ImageContainer>
        </GuideStep>
      </GuideContainer>
      <ScopeButton
        type="default"
        onClick={handleClose}
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          width: 'auto',
          height: 28,
          fontSize: '12px',
          padding: '0 12px',
        }}
      >
        {t('common.close')}
      </ScopeButton>
    </StyledCard>
  );
};
